import { type SetURLSearchParams } from 'react-router'
import { analytics } from '../Analytics/Analytics'
import { getUrlParam } from '../../utils/UrlUtils'
import { getUserCartApi, saveUserCartApi } from '../../api/userCart.api'
import StorageManager from '../StorageManager'
import { v4 as uuid } from 'uuid'
import { logger } from '../logger'
import axios from 'axios'
import { type FLOWS, FLOW_TO_KEY } from './userCart.consts'
import { type TilePreviewSpec } from '@mixtiles/web-backend-shared'

// The cart id param is a one-shot restore token; clear it once the flow ran so
// navigating back / refreshing can't re-trigger a restore.
export function clearCartRestoreParam(
  flow: FLOWS,
  setSearchParams: SetURLSearchParams
) {
  const flowKey = FLOW_TO_KEY[flow]
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev)
      next.delete(flowKey)
      return next
    },
    { replace: true }
  )
}

export function clearCartId(flow: FLOWS) {
  StorageManager.remove(FLOW_TO_KEY[flow])
}

const MIN_CART_ID_LENGTH = 20

// Storage can hand back invalid truthy values (such as "None", "null", true etc.),
// so presence is not enough to treat a stored id as a real cart.
function isValidCartId(cartId: unknown): boolean {
  return !!cartId && String(cartId).length >= MIN_CART_ID_LENGTH
}

export function hasStoredCartId(flow: FLOWS): boolean {
  return isValidCartId(StorageManager.get(FLOW_TO_KEY[flow]))
}

function getCartIdForFlow(flow: FLOWS): string | null {
  const cartIdStorageKey = FLOW_TO_KEY[flow]
  if (!isValidCartId(StorageManager.get(cartIdStorageKey))) {
    StorageManager.set(cartIdStorageKey, uuid())
  }
  return StorageManager.get<string>(cartIdStorageKey)
}

/**
 * Send the user's cart to Braze, so we'd be able to restore it in certain email flows (such as abandon cart).
 * In order to do it, we upload the cart items to backend and send the cartId referencing them to Braze by setting user properties.
 * This cartId can be used later to restore the cart (using restoreCart())
 *
 * @param flow - a value from FLOWS as there are different types of carts (tile designer, wall designer, art etc.)
 * @param cartItems - items to store on the backend that need restoration
 * @param emailTemplateParams - parameters that will be used for generating the email
 * in Braze (according to marketing definitions). For example title, price, imageUrl etc.
 */
export async function saveCart({
  flow,
  cartItems,
  emailTemplateParams,
  previewItems,
  tilePreviewSpecs,
  photoBookCoverUrl,
}: {
  flow: FLOWS
  cartItems: any[]
  emailTemplateParams: Record<string, any>
  previewItems?: unknown[]
  tilePreviewSpecs?: TilePreviewSpec[]
  photoBookCoverUrl?: string
}) {
  const cartId = getCartIdForFlow(flow)
  try {
    await saveUserCartApi({
      cartId,
      cartItems,
      previewItems,
      tilePreviewSpecs,
      photoBookCoverUrl,
      photoBookCoverCaption: emailTemplateParams['Cover Caption'],
      aiPreviews: emailTemplateParams.aiPreviews,
    })

    // We save all entries of emailTemplateParams as key-value pairs, because email templates can't read nested objects.
    const emailParams = { cartId, ...emailTemplateParams }
    const templateParamsWithPrefix = Object.fromEntries(
      Object.entries(emailParams).map(([k, v]) => [
        `User Cart - ${flow} Flow - ${k}`,
        v,
      ])
    )
    analytics.setUserProperties(templateParamsWithPrefix)
  } catch (e) {
    // No response means the request never reached the backend (dead connection,
    // ad blocker) — the client's environment, not our failure.
    if (axios.isAxiosError(e) && !e.response) {
      logger.warning('failed to save cart', {
        cartId,
        flow,
        errorMessage: e.message,
      })
    } else {
      logger.error('failed to save cart', e, { cartId, flow })
    }
  }
}

export async function restoreCart(flow: FLOWS) {
  const flowKey = FLOW_TO_KEY[flow]
  const cartId = getUrlParam(flowKey)
  if (cartId) {
    try {
      const userCart = await getUserCartApi(cartId)
      StorageManager.set(flowKey, cartId)
      return { cartId, userCart }
    } catch (e) {
      const currentCartId = getCartIdForFlow(flow)
      if (cartId !== currentCartId) {
        const isAlreadyRestored =
          axios.isAxiosError(e) && e.response?.status === 409
        if (isAlreadyRestored) {
          logger.warning('failed to restore cart (conflict)', {
            error: e,
            cartId,
            flow,
          })
        } else {
          logger.error('failed to restore cart', e, { cartId, flow })
        }
      }
    }
  }
  return { cartId, userCart: null }
}
